import React, { useEffect } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { PWAInstallPrompt } from "@/components/common/PWAInstallPrompt";
import { initializeNative, isNative } from "@/lib/capacitor";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ScannerPage from "@/pages/ScannerPage";
import SearchPage from "@/pages/SearchPage";
import UploadPage from "@/pages/UploadPage";
import AnalysisPage from "@/pages/AnalysisPage";
import HistoryPage from "@/pages/HistoryPage";
import CommunityPage from "@/pages/CommunityPage";
import ProfilePage from "@/pages/ProfilePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import CompanyPage from "@/pages/CompanyPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import FestivalDashboard from "@/pages/FestivalDashboard";
import FestivalManage from "@/pages/FestivalManage";
import VendorDisplay from "@/pages/VendorDisplay";
import AboutPage from "@/pages/AboutPage";
import CompanyScanPage from "@/pages/CompanyScanPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import StoresPage from "@/pages/StoresPage";
import StoreScanPage from "@/pages/StoreScanPage";
import BrowseStoresPage from "@/pages/BrowseStoresPage";
import InvestorsPage from "@/pages/InvestorsPage";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/investors" element={<InvestorsPage />} />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/scan" element={
        <ProtectedRoute>
          <ScannerPage />
        </ProtectedRoute>
      } />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/upload" element={
        <ProtectedRoute>
          <UploadPage />
        </ProtectedRoute>
      } />
      <Route path="/analysis/:id" element={
        <ProtectedRoute>
          <AnalysisPage />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <HistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <CommunityPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/company" element={
        <ProtectedRoute>
          <CompanyPage />
        </ProtectedRoute>
      } />
      <Route path="/festival" element={
        <ProtectedRoute>
          <FestivalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/festival/:festivalId" element={
        <ProtectedRoute>
          <FestivalManage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/stores" element={
        <ProtectedRoute>
          <StoresPage />
        </ProtectedRoute>
      } />
      
      {/* Public vendor display - no auth required */}
      <Route path="/vendor/:vendorId/display" element={<VendorDisplay />} />
      
      {/* Public company scan page - no auth required (for QR codes) */}
      <Route path="/company/:companyId/scan" element={<CompanyScanPage />} />
      
      {/* Public store scan page - no auth required (for QR codes) */}
      <Route path="/store/:storeId/scan" element={<StoreScanPage />} />
      
      {/* Public browse stores page - no auth required */}
      <Route path="/browse-stores" element={<BrowseStoresPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize native features when running as mobile app
  useEffect(() => {
    initializeNative();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-background">
          <AppRoutes />
          <Toaster position="top-right" richColors />
          {/* Only show PWA install prompt on web, not native */}
          {!isNative() && <PWAInstallPrompt />}
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
